// assets/subscription-box-builder.js
//
// Box builder for the monthly soup subscription. Replaces a free-text field that
// (a) could not hold a 16-soup selection (maxlength 250 vs ~289 chars typical),
// (b) validated nothing, and (c) named no soups, so a customer had to already know
// the catalogue. See docs/superpowers/specs/2026-09-29-subscription-box-builder-design.md
//
// The required count is read from the selected variant's Box size option ("9 soups" -> 9)
// rather than a block setting, so it cannot drift out of step with what was paid.
import { Component } from '@theme/component';

/**
 * @typedef {object} SubscriptionBoxBuilderRefs
 * @property {HTMLElement} counter
 * @property {HTMLInputElement} hiddenInput
 */

/** @extends Component<SubscriptionBoxBuilderRefs> */
class SubscriptionBoxBuilder extends Component {
  /** @type {Map<string, number>} handle -> quantity */
  #counts = new Map();
  /** @type {number} 0 means "unknown" and keeps the form gated shut */
  #required = 0;
  /** @type {MutationObserver|null} */
  #observer = null;
  #reassertQueued = false;
  /** Modal is opt-out: display_mode="inline" falls back to the flat grid. */
  #modal = false;
  #open = false;
  /** @type {Element|null} element focus returns to when the modal closes */
  #returnFocus = null;
  /** auto = the soup count drives the variant; pick-first = the dropdown does. */
  #auto = false;
  /** @type {Map<number,string>} soup count -> variant id */
  #variants = new Map();
  #min = 0;
  #max = 0;
  /** Count we are mid-way through pushing to the variant picker; gate stays shut until it lands. */
  #syncingTo = null;
  /** @type {ReturnType<typeof setTimeout>|null} */
  #syncTimer = null;
  /** connectedCallback can run more than once — the theme re-inserts this element on a
   *  variant re-render. Without this guard each run stacked another click listener, so a
   *  single click incremented 2x, then 4x. Observed live: 7 clicks produced 15 soups. */
  #wired = false;
  /** @type {ReturnType<typeof setTimeout>|null} */
  #errorTimer = null;

  connectedCallback() {
    super.connectedCallback?.();
    if (this.#wired) { this.#render(); return; }   // already listening; just repaint
    this.#wired = true;
    this.#required = this.#parseSize(this.dataset.initialVariantTitle);

    // Delegated rather than declarative: one listener, survives re-render of the grid.
    // Handlers are arrow class fields, so they are already bound — a private METHOD
    // cannot be reassigned (read-only), which is why they are not declared as methods.
    this.#modal = this.dataset.displayMode === 'modal';
    this.#auto = this.dataset.sizeMode === 'auto';
    try {
      const map = JSON.parse(this.dataset.variantMap || '{}');
      for (const [count, id] of Object.entries(map)) this.#variants.set(Number(count), String(id));
    } catch { /* leaves the map empty, which disables auto mode below */ }
    if (this.#variants.size) {
      const counts = [...this.#variants.keys()];
      this.#min = Math.min(...counts);
      this.#max = Math.max(...counts);
    } else {
      this.#auto = false;   // no map, no auto mode — fail back to the dropdown
    }
    this.addEventListener('click', this.#onClick);
    document.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('variant:update', this.#onVariantUpdate);
    this.#form()?.addEventListener('submit', this.#onSubmit, true);

    // The theme re-enables the add-to-cart button when the variant changes, undoing
    // the gate. Watch the form and re-assert it. The submit guard is the real backstop;
    // this keeps the button's appearance honest.
    const form = this.#form();
    if (form && typeof MutationObserver !== 'undefined') {
      this.#observer = new MutationObserver(() => {
        if (this.#reassertQueued) return;
        this.#reassertQueued = true;
        requestAnimationFrame(() => {
          this.#reassertQueued = false;
          // MUST go through #isValid. This previously inlined the pick-first rule, so in
          // auto mode it re-disabled the button every time #render enabled it — the two
          // definitions of "valid" disagreed and the observer always won.
          this.#gate(this.#isValid(this.#total()));
        });
      });
      this.refs.dialog?.addEventListener?.('close', () => {
      // Esc handled by the UA closes the dialog without going through #closeModal.
      this.#open = false;
    });
    this.#observer.observe(form, { subtree: true, childList: true,
                                     attributes: true, attributeFilter: ['disabled'] });
    }

    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#wired = false;
    this.#observer?.disconnect();
    if (this.#syncTimer) clearTimeout(this.#syncTimer);
    if (this.#errorTimer) clearTimeout(this.#errorTimer);
    if (this.#open) this.#closeModal();
    document.removeEventListener('keydown', this.#onKeydown);
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener('variant:update', this.#onVariantUpdate);
    this.#form()?.removeEventListener('submit', this.#onSubmit, true);
  }

  // NOTE: items are selected by [data-box-item], never [data-handle] — the +/- buttons
  // also carry data-handle, and matching those produced "undefined xN" in the summary.

  /** "9 soups" -> 9. Unparseable -> 0, which fails closed. */
  #parseSize(text) {
    const m = String(text ?? '').match(/(\d+)/);
    return m ? parseInt(m[1], 10) : 0;
  }

  #form() {
    return document.getElementById(this.dataset.formId);
  }

  #total() {
    let t = 0;
    for (const v of this.#counts.values()) t += v;
    return t;
  }

  #onClick = (event) => {
    if (this.#modal) {
      if (event.target.closest('[data-open-box]')) { event.preventDefault(); this.#openModal(); return; }
      // Backdrop carries data-close-box too, so a click outside the panel dismisses it.
      if (event.target.closest('[data-submit-box]')) {
        event.preventDefault();
        // Woo completed the purchase inside the popup. Mirror that: close, then add.
        //
        // The theme's cart drawer is ITSELF a modal <dialog>. Closing ours and opening
        // the drawer in the same tick leaves two dialogs transitioning through the top
        // layer at once, which iOS WebKit renders as an EMPTY drawer (the add itself
        // succeeds — the cart page and checkout are correct). Desktop Chrome tolerates
        // it, which is why it only showed up on a phone. Yield a frame so our dialog is
        // fully out of the top layer before the theme puts its own in.
        if (!this.#isValid(this.#total())) { this.#showError(); return; }
        this.#closeModal();
        const addButton = this.#form()?.querySelector('button[name="add"]');
        requestAnimationFrame(() => addButton?.click());
        return;
      }
      if (event.target.closest('[data-close-box]')) { event.preventDefault(); this.#closeModal(); return; }
    }
    const button = event.target.closest('[data-step]');
    if (!button || !this.contains(button) || button.disabled) return;
    event.preventDefault();

    const handle = button.dataset.handle;
    const delta = parseInt(button.dataset.step, 10);
    const current = this.#counts.get(handle) ?? 0;

    // A single soup can never push the box past its ceiling.
    const ceiling = this.#auto ? this.#max : this.#required;
    if (delta > 0 && this.#total() >= ceiling) return;

    const next = Math.max(0, current + delta);
    if (next === current) return;
    this.#counts.set(handle, next);
    this.#render();
  };

  #onVariantUpdate = (event) => {
    // Horizon 3.1.0 puts the new variant on detail.RESOURCE. The JSDoc in
    // assets/events.js shows detail.variant, which is stale — verified by probing a
    // real variant:update on the storefront. Both are read so either shape works.
    const d = event.detail ?? {};
    const resource = d.resource ?? d.variant;
    const next = this.#parseSize(resource?.title ?? resource?.option1);
    if (!next) return;
    if (this.#auto) {
      // Count is the source of truth here. The variant is an OUTPUT, so adopting it as
      // input would make the component fight its own change.
      if (this.#syncingTo === next) this.#syncingTo = null;
      this.#render();
      return;
    }
    if (next === this.#required) return;
    this.#required = next;
    // Deliberately keep the existing picks. Dropping 9 -> 5 with 9 chosen leaves the
    // customer over the limit and the button disabled, with the counter saying why.
    this.#render();
  };

  /**
   * The app widget's own selling_plan input is not associated with the form, so it never
   * submits. We emit our own from Liquid. If the app ever does populate its input (more
   * than one plan), prefer that value — it reflects what the customer actually chose.
   */
  #syncSellingPlan() {
    const ours = this.refs.sellingPlanInput;
    if (!ours) return;
    const theirs = document.querySelector(
      '.subscriptions_app_embed_block [name="selling_plan"], .selected-selling-plan-id'
    );
    const v = theirs && 'value' in theirs ? String(theirs.value || '') : '';
    if (v && v !== ours.value) ours.value = v;
  }

  #onSubmit = (event) => {
    this.#syncSellingPlan();
    if (this.#total() !== this.#required || this.#required === 0) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.refs.counter?.scrollIntoView({ block: 'center' });
    }
  };

  /** Woo's own messages, so the store reads the same as it did before the move. */
  #showError() {
    const el = this.refs.error;
    if (!el) return;
    const total = this.#total();
    const min = this.#auto ? this.#min : this.#required;
    const max = this.#auto ? this.#max : this.#required;
    let msg;
    if (total === 0) msg = 'No products selected. Please add at least one product.';
    else if (total < min) msg = `Please select at least ${min} products in this step.`;
    else if (total > max) msg = `You can select maximum ${max} products in this step.`;
    else msg = 'One moment – updating your box size.';
    el.textContent = msg;
    el.hidden = false;
    clearTimeout(this.#errorTimer);
    this.#errorTimer = setTimeout(() => { el.hidden = true; }, 5000);
  }

  #onKeydown = (event) => {
    if (event.key === 'Escape' && this.#open) { event.preventDefault(); this.#closeModal(); }
  };

  #openModal() {
    const dialog = this.refs.dialog;
    if (!dialog || this.#open) return;
    this.#returnFocus = document.activeElement;
    // showModal() puts it in the top layer, above the sticky header. Fall back to the
    // attribute for anything that lacks it.
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else { dialog.hidden = false; dialog.setAttribute('open', ''); }
    this.#open = true;
    // NO manual body scroll lock. showModal() already blocks background scroll via the
    // top layer, and `body { overflow: hidden }` is a known cause of layout collapse on
    // iOS — writing it here was this component's only global side-effect, and the theme's
    // cart drawer (also a modal <dialog>) rendered its summary off-screen on iPhone.
    (this.refs.closeButton ?? dialog).focus?.();
  }

  #closeModal() {
    const dialog = this.refs.dialog;
    if (!dialog || !this.#open) return;
    if (typeof dialog.close === 'function' && dialog.open) dialog.close();
    else { dialog.hidden = true; dialog.removeAttribute('open'); }
    this.#open = false;
    // Duck-typed: `instanceof HTMLElement` fails across frames and in test harnesses.
    if (typeof this.#returnFocus?.focus === 'function') this.#returnFocus.focus();
    this.#returnFocus = null;
  }

  /** In auto mode the variant must catch up before the box can be bought. */
  #isValid(total) {
    if (!this.#auto) return this.#required > 0 && total === this.#required;
    if (total < this.#min || total > this.#max) return false;
    return this.#syncingTo === null && this.#currentVariantCount() === total;
  }

  #currentVariantCount() {
    const form = this.#form();
    const id = form?.querySelector('[name="id"]')?.value;
    if (!id) return null;
    for (const [count, vid] of this.#variants) if (String(vid) === String(id)) return count;
    return null;
  }

  /** Push the count onto the variant picker, debounced so 5->16 is one re-render. */
  #scheduleVariantSync(total) {
    if (!this.#auto) return;
    if (this.#syncTimer) clearTimeout(this.#syncTimer);
    if (total < this.#min || total > this.#max) { this.#syncingTo = null; return; }
    if (this.#currentVariantCount() === total) { this.#syncingTo = null; return; }
    this.#syncingTo = total;
    this.#syncTimer = setTimeout(() => {
      const select = document.querySelector('select[name="options[Box size]"]');
      const option = select && [...select.options].find((o) => this.#parseSize(o.value) === total);
      if (!option) { this.#syncingTo = null; this.#render(); return; }
      select.value = option.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }, 350);
  }

  #render() {
    const total = this.#total();
    const valid = this.#isValid(total);
    const full = total >= (this.#auto ? this.#max : this.#required);

    for (const item of this.querySelectorAll('[data-box-item]')) {
      const qty = this.#counts.get(item.dataset.handle) ?? 0;
      const display = item.querySelector('[data-count-display]');
      if (display) display.textContent = String(qty);
      item.toggleAttribute('data-chosen', qty > 0);

      const plus = item.querySelector('[data-step="1"]');
      if (plus) plus.disabled = item.hasAttribute('data-sold-out') || full;
      const minus = item.querySelector('[data-step="-1"]');
      if (minus) minus.disabled = qty === 0;
    }

    const { counter, hiddenInput } = this.refs;
    if (counter) {
      const template = this.#auto
        ? (counter.dataset.templateAuto || '[current] selected (choose [min]–[max])')
        : (counter.dataset.template || '[current] of [max] selected');
      counter.textContent = template
        .replace('[current]', String(total))
        .replace('[min]', String(this.#min))
        .replace('[max]', String(this.#auto ? this.#max : this.#required));
      counter.toggleAttribute('data-valid', valid);
      counter.toggleAttribute('data-over', total > this.#required);
    }
    if (hiddenInput) hiddenInput.value = this.#summary();

    // In modal mode the grid is hidden, so the page needs its own read-out.
    const { summary } = this.refs;
    if (summary) {
      const chosen = this.#summary();
      if (this.#auto) {
        summary.textContent = chosen
          ? `${total} soup${total === 1 ? '' : 's'} – ${chosen}`
          : `Choose ${this.#min}–${this.#max} soups`;
      } else {
        summary.textContent = chosen
          ? `${total} of ${this.#required} selected – ${chosen}`
          : `Choose ${this.#required} soup${this.#required === 1 ? '' : 's'}`;
      }
      summary.toggleAttribute('data-valid', valid);
    }

    const { total: totalEl } = this.refs;
    if (totalEl) {
      const unit = Number(this.dataset.unitPrice || 0);
      totalEl.textContent = unit ? this.#money(unit * total) : '';
    }

    this.#gate(valid);
    this.#scheduleVariantSync(total);
  }

  #money(cents) {
    const fmt = this.dataset.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    return fmt.replace(/\{\{\s*amount\s*\}\}/, amount);
  }

  /** "Creamy Gnocchi x2, Tom Kha x1" — grid order, not click order, so it reads consistently. */
  #summary() {
    const parts = [];
    for (const item of this.querySelectorAll('[data-box-item]')) {
      const qty = this.#counts.get(item.dataset.handle) ?? 0;
      if (qty > 0) parts.push(qty > 1 ? `${item.dataset.title} x${qty}` : item.dataset.title);
    }
    return parts.join(', ');
  }

  /** Gate Add to cart and any accelerated checkout button sharing the form. */
  #gate(valid) {
    const form = this.#form();
    if (!form) return;
    // Write ONLY on change. The MutationObserver below watches this same subtree, so an
    // unconditional write re-triggers it and loops until the renderer locks up.
    const setDisabled = (el, want) => { if (el.disabled !== want) el.disabled = want; };
    for (const el of form.querySelectorAll('button[name="add"]')) setDisabled(el, !valid);
    const accelerated = form.querySelector('.shopify-payment-button');
    if (accelerated) {
      if (accelerated.hasAttribute('data-blocked') === valid) {
        accelerated.toggleAttribute('data-blocked', !valid);
      }
      for (const el of accelerated.querySelectorAll('button')) setDisabled(el, !valid);
    }
  }
}

customElements.define('subscription-box-builder-component', SubscriptionBoxBuilder);
